import { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@features/authentication/store/authStore';
import { HowItWorksDialog } from './HowItWorksDialog';

/**
 * First-run welcome strip (86d3rrfdk) — dismissible, persisted PER USER so a one-time nudge doesn't
 * follow someone to every browser or nag after they've dismissed it. Also the entry point to the
 * reopenable "How it works" guide (kept mounted so the guide can be opened after dismissal too — the
 * button lives here, but the guide state is lifted so it survives the strip going away).
 */
export function WelcomeStrip() {
  const userId = useAuthStore((s) => s.user?.userId ?? 'anon');
  const storageKey = `dc-welcome-dismissed-${userId}`;

  const [dismissed, setDismissed] = useState(() => localStorage.getItem(storageKey) === 'true');
  const [guideOpen, setGuideOpen] = useState(false);

  const dismiss = () => {
    localStorage.setItem(storageKey, 'true');
    setDismissed(true);
  };

  return (
    <>
      {!dismissed && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-info/25 bg-info/8 p-4">
          <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-info" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Document Capture mostly runs itself</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Scans are read, filed and linked into NetSuite automatically. You only step in for the rare
              flagged item.
            </p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setGuideOpen(true)}>
                Show me how
              </Button>
              <Button size="sm" variant="ghost" onClick={dismiss}>
                Got it
              </Button>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={dismiss}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      )}

      <HowItWorksDialog open={guideOpen} onOpenChange={setGuideOpen} />
    </>
  );
}
